import React, { useState, useRef, useEffect, useMemo } from 'react';
import { FiChevronDown, FiChevronUp, FiPlus, FiTrash2 } from 'react-icons/fi';
import st from './SearchSelect.module.css';

export default function SearchSelect({
  options = [], value, onChange, placeholder = 'Select…',
  searchPlaceholder = 'Search…', width = '200px', visibleCount = 10, onOpen,
  // Optional: creatable behaviour. Press Enter on a value not present to create it.
  onCreate,            // async (name: string) => option | void
  createLabel = 'Create',
  // Optional: per-item delete handler. Renders a small × per row.
  onDelete,            // async (option) => void
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  const filtered = useMemo(() => {
    if (!search) return options;
    const l = search.toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(l) || String(o.value).toLowerCase().includes(l));
  }, [options, search]);

  const selectedLabel = options.find(o => o.value === value)?.label ?? value;

  // Creatable hint visible when typed value has no exact label match (case-insensitive).
  const trimmed = search.trim();
  const exactMatch = trimmed && options.some(o => String(o.label).toLowerCase() === trimmed.toLowerCase());
  const showCreate = !!onCreate && !!trimmed && !exactMatch && !busy;

  const toggle = () => {
    setOpen(prev => { if (prev) setSearch(''); else onOpen?.(); return !prev; });
  };

  const select = (val) => { onChange(val); setSearch(''); setOpen(false); };

  const handleCreate = async () => {
    if (!showCreate) return;
    setBusy(true);
    try {
      const created = await onCreate(trimmed);
      // If parent returned an option-like { value }, auto-select it.
      if (created && created.value != null) onChange(created.value);
      setSearch('');
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (e, option) => {
    e.stopPropagation();
    if (!onDelete) return;
    setBusy(true);
    try {
      await onDelete(option);
      // If the deleted option was selected, clear the value.
      if (option.value === value) onChange('');
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (showCreate) {
        handleCreate();
      } else if (filtered.length === 1) {
        select(filtered[0].value);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className={st.wrapper} ref={ref} style={{ width }}>
      <button type="button" className={`${st.trigger} ${open ? st.triggerOpen : ''}`} onClick={toggle}>
        <span className={value ? undefined : st.placeholder}>{value ? selectedLabel : placeholder}</span>
        <span className={st.arrow}>{open ? <FiChevronUp /> : <FiChevronDown />}</span>
      </button>
      {open && (
        <div className={st.dropdown}>
          <div className={st.searchBox}>
            <input ref={inputRef} className={st.searchInput} placeholder={searchPlaceholder}
              value={search} onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()} onKeyDown={onKeyDown} disabled={busy} />
          </div>
          <ul className={st.menu} style={{ maxHeight: visibleCount * 34 }}>
            {showCreate && (
              <li className={st.item} onClick={handleCreate}
                style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--accent)', fontWeight: 500 }}>
                <FiPlus size={13} /> {createLabel} “{trimmed}”
                <span style={{ marginLeft: 'auto', fontSize: '0.65rem', color: 'var(--text-muted)' }}>Enter ↵</span>
              </li>
            )}
            {filtered.length > 0 ? filtered.map(o => (
              <li key={o.value} className={`${st.item} ${o.value === value ? st.itemActive : ''}`}
                onClick={() => select(o.value)}
                style={onDelete ? { display: 'flex', alignItems: 'center', gap: 8 } : undefined}>
                <span style={{ flex: 1 }}>{o.label}</span>
                {onDelete && (
                  <button type="button" onClick={(e) => handleDelete(e, o)}
                    title="Delete"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e06c75', padding: 2, display: 'inline-flex' }}>
                    <FiTrash2 size={12} />
                  </button>
                )}
              </li>
            )) : (!showCreate && <li className={st.noResults}>No results</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

export function MultiSearchSelect({
  options = [], selected = [], onChange, placeholder = 'Select…',
  searchPlaceholder = 'Search…', width = '250px', onOpen,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  const filtered = useMemo(() => {
    if (!search) return options;
    const l = search.toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(l));
  }, [options, search]);

  const toggle = () => { setOpen(prev => { if (prev) setSearch(''); else onOpen?.(); return !prev; }); };
  const toggleItem = (val) => {
    if (selected.includes(val)) onChange(selected.filter(v => v !== val));
    else onChange([...selected, val]);
  };
  const remove = (e, val) => { e.stopPropagation(); onChange(selected.filter(v => v !== val)); };

  return (
    <div className={st.wrapper} ref={ref} style={{ width }}>
      <button type="button" className={`${st.trigger} ${open ? st.triggerOpen : ''}`} onClick={toggle}
        style={{ minHeight: 34, height: 'auto' }}>
        <span style={{ display: 'flex', flexWrap: 'wrap', gap: 3, flex: 1 }}>
          {selected.length > 0 ? (
            <div className={st.pills}>
              {selected.map(v => {
                const lbl = options.find(o => o.value === v)?.label || v;
                return <span key={v} className={st.pill}>{lbl}<span className={st.pillRemove} onClick={(e) => remove(e, v)}>×</span></span>;
              })}
            </div>
          ) : <span className={st.placeholder}>{placeholder}</span>}
        </span>
        <span className={st.arrow}>{open ? <FiChevronUp /> : <FiChevronDown />}</span>
      </button>
      {open && (
        <div className={st.dropdown}>
          <div className={st.searchBox}>
            <input ref={inputRef} className={st.searchInput} placeholder={searchPlaceholder}
              value={search} onChange={(e) => setSearch(e.target.value)} onClick={(e) => e.stopPropagation()} />
          </div>
          <ul className={st.menu} style={{ maxHeight: 240 }}>
            {filtered.length > 0 ? filtered.map(o => (
              <li key={o.value} className={`${st.item} ${selected.includes(o.value) ? st.itemActive : ''}`}
                onClick={() => toggleItem(o.value)}>
                <span className={st.checkMark}>{selected.includes(o.value) ? '☑' : '☐'}</span>{o.label}
              </li>
            )) : <li className={st.noResults}>No results</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

